import React, { useState, useEffect, useContext } from 'react';
import { 
  Box, Heading, SimpleGrid, Text, Button, Flex, 
  useColorModeValue, Badge, Icon, Avatar, HStack 
} from '@chakra-ui/react';
import { MdVideoCameraFront, MdGroups, MdOutlineTimer } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../services/axiosInstance';
import AuthContext from '../services/AuthContext';

export default function ActiveMeetings() {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.100", "gray.700");
  
  const headingColor = useColorModeValue("gray.800", "white");
  const itemBorderColor = useColorModeValue("gray.200", "gray.700");
  const itemBgColor = useColorModeValue("white", "gray.900");
  const instructorBg = useColorModeValue("gray.50", "gray.800");

  const fetchMeetings = async () => {
    try {
      const response = await axiosInstance.get(`/meetings/active`);
      if (response.data.success) {
        setMeetings(response.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch active meetings", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMeetings();
    const interval = setInterval(fetchMeetings, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleJoin = (roomName) => {
    navigate(`/meet/${roomName}`);
  };

  const handleEnd = async (roomName) => {
    try {
      await axiosInstance.post(`/meetings/end/${encodeURIComponent(roomName)}`);
      fetchMeetings();
    } catch (err) {
      console.error("Failed to end meeting", err);
    }
  };

  if (loading && meetings.length === 0) return null;

  if (meetings.length === 0) {
    return (
      <Flex 
        direction="column" 
        align="center" 
        justify="center" 
        p={10} 
        bg={bgColor} 
        borderRadius="2xl" 
        borderWidth="1px"
        borderColor={borderColor}
        borderStyle="dashed"
      >
        <Box p={4} bg="blue.50" rounded="full" color="blue.600" mb={4}>
          <Icon as={MdOutlineTimer} w={10} h={10} />
        </Box>
        <Heading size="md" color="gray.600" mb={2} dir="rtl">لا توجد حصص نشطة حالياً</Heading>
        <Text color="gray.500" dir="rtl">الرجاء الانتظار حتى يقوم المدرس ببدء الحصة</Text>
      </Flex>
    );
  }

  return (
    <Box p={{ base: 4, md: 8 }} shadow="xl" borderWidth="1px" borderColor={borderColor} borderRadius="3xl" bg={bgColor} width="100%">
      <Flex justify="space-between" align="center" mb={8} dir="rtl">
        <Flex align="center" gap={3}>
          <Box position="relative">
            <Icon as={MdVideoCameraFront} w={9} h={9} color="red.500" />
            <Box position="absolute" top={-1} right={-1} w={3.5} h={3.5} bg="red.500" rounded="full" className="pulse-animation" />
          </Box>
          <Box>
            <Heading size="lg" color={headingColor} fontWeight="bold">
              الحصص الجارية الآن
            </Heading>
            <Text fontSize="sm" color="gray.500">اختر حصة وانضم إليها مباشرة</Text>
          </Box>
        </Flex>
        <Badge colorScheme="red" variant="solid" p={2.5} borderRadius="xl" fontSize="sm" fontWeight="bold">
          {meetings.length} حصة مباشرة
        </Badge>
      </Flex>

      {/* Cards Grid Layout */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6} dir="rtl">
        {meetings.map((meeting) => {
          const isOwnerOrAdmin = user?.role === 'admin' || user?.role === 'instructor' || user?._id === meeting.createdBy?._id || user?.id === meeting.createdBy?._id;
          
          return (
            <Flex 
              key={meeting._id} 
              direction="column"
              justify="space-between"
              p={6} 
              borderWidth="1px" 
              borderColor={itemBorderColor}
              borderRadius="2xl" 
              bg={itemBgColor}
              boxShadow="sm"
              transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
              position="relative"
              overflow="hidden"
              _hover={{ 
                transform: "translateY(-6px)", 
                boxShadow: "2xl",
                borderColor: "blue.400"
              }}
            >
              <Box>
                {/* Top Badge Info Bar */}
                <Flex justify="space-between" align="center" mb={4}>
                  <Badge colorScheme="red" variant="solid" px={3} py={1} borderRadius="full" fontSize="xs" display="flex" alignItems="center" gap={1.5}>
                    <Box w={2} h={2} bg="white" borderRadius="full" className="pulse-animation" />
                    مباشر الآن
                  </Badge>
                  <Badge colorScheme="blue" variant="subtle" px={2.5} py={1} borderRadius="lg" fontSize="xs" fontWeight="bold">
                    ID: {meeting.meetingId}
                  </Badge>
                </Flex>

                {/* Meeting Room Title */}
                <Heading size="md" color={headingColor} mb={4} minH="50px" lineHeight="1.4" fontWeight="bold">
                  {meeting.roomName}
                </Heading>

                {/* Instructor Info Box */}
                <Flex align="center" gap={3} p={3.5} bg={instructorBg} borderRadius="xl" border="1px solid" borderColor="blackAlpha.50">
                  <Avatar size="sm" name={meeting.createdBy?.username} bg="blue.500" color="white" />
                  <Box overflow="hidden">
                    <Text fontSize="11px" color="gray.500" fontWeight="medium">المدرس / المحاضر</Text>
                    <Text fontSize="sm" fontWeight="bold" color={headingColor} isTruncated>
                      {meeting.createdBy?.username || 'غير معروف'}
                    </Text>
                  </Box>
                </Flex>
              </Box>

              {/* Action Buttons */}
              <HStack spacing={3} mt={6} pt={4} borderTop="1px solid" borderColor="gray.100">
                <Button 
                  colorScheme="blue" 
                  size="md" 
                  onClick={() => handleJoin(meeting.meetingId)}
                  borderRadius="xl"
                  flex={1}
                  fontWeight="bold"
                  boxShadow="0 4px 14px 0 rgba(49, 130, 206, 0.35)"
                  _hover={{ transform: "translateY(-2px)", boxShadow: "0 6px 20px rgba(49, 130, 206, 0.5)" }}
                >
                  انضمام للحصة
                </Button>
                {isOwnerOrAdmin && (
                  <Button 
                    colorScheme="red" 
                    variant="outline"
                    size="md" 
                    onClick={() => handleEnd(meeting.roomName)}
                    borderRadius="xl"
                    px={4}
                    _hover={{ bg: "red.50" }}
                  >
                    إنهاء
                  </Button>
                )}
              </HStack>
            </Flex>
          );
        })}
      </SimpleGrid>
      
      <style>
        {`
          @keyframes pulse {
            0% { transform: scale(0.95); opacity: 0.8; }
            50% { transform: scale(1.1); opacity: 1; }
            100% { transform: scale(0.95); opacity: 0.8; }
          }
          .pulse-animation {
            animation: pulse 1.8s infinite ease-in-out;
          }
        `}
      </style>
    </Box>
  );
}

